import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Search, Check } from "lucide-react";
import { formatDateShort } from "@/lib/date";

/**
 * ArticlePicker — dialog to choose one or many published articles.
 * Props:
 *  - open, onOpenChange
 *  - multi (bool) default true
 *  - excludeIds (string[]) — filter out already selected
 *  - onSelect(article | article[])
 */
export default function ArticlePicker({ open, onOpenChange, multi = true, excludeIds = [], onSelect, title = "Elegir noticias" }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      api.categories().then(setCategories).catch(() => setCategories([]));
      setSelected(new Set());
    }
  }, [open]);

  const load = React.useCallback(() => {
    setLoading(true);
    api.adminListArticles({
      q: q || undefined,
      category: category || undefined,
      status: "published",
      limit: 50,
    })
      .then((r) => setItems(r.items))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [q, category]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [q, category, open, load]);

  const excludeSet = useMemo(() => new Set(excludeIds), [excludeIds]);
  const filtered = items.filter((a) => !excludeSet.has(a.id));

  const toggle = (id) => {
    if (multi) {
      setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    } else {
      setSelected(new Set([id]));
    }
  };

  const confirm = () => {
    const chosen = filtered.filter((a) => selected.has(a.id));
    if (chosen.length === 0) return;
    if (multi) onSelect(chosen);
    else onSelect(chosen[0]);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-serif">{title}</DialogTitle>
          <DialogDescription>Sólo se listan noticias publicadas.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
          <div className="relative md:col-span-2">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-textgray" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por título o autor…"
              className="pl-9 h-9 bg-lightgray border-softblue text-sm"
            />
          </div>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="h-9 bg-lightgray border border-softblue px-3 text-sm text-nightblue"
          >
            <option value="">Todas las categorías</option>
            {categories.map((c) => (
              <option key={c.id} value={c.slug}>{c.name}</option>
            ))}
          </select>
        </div>

        <div className="flex-1 overflow-auto border border-softblue divide-y divide-softblue">
          {loading ? (
            <div className="p-6 text-center text-sm text-textgray">Cargando…</div>
          ) : filtered.length === 0 ? (
            <div className="p-6 text-center text-sm text-textgray">No hay más noticias.</div>
          ) : filtered.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => toggle(a.id)}
              className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm hover:bg-lightgray transition-colors ${
                selected.has(a.id) ? "bg-watergreen/10" : "bg-white"
              }`}
            >
              <div className="w-12 h-12 bg-lightgray flex-shrink-0 overflow-hidden">
                {a.main_image && <img src={a.main_image} alt="" className="w-full h-full object-cover" loading="lazy" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-blueblack line-clamp-2">{a.title}</div>
                <div className="text-xs text-textgray">{a.main_category_name} · {a.author_name} · {formatDateShort(a.published_at)}</div>
              </div>
              <span className={`w-5 h-5 border flex items-center justify-center flex-shrink-0 ${
                selected.has(a.id) ? "bg-watergreen border-watergreen" : "border-softblue"
              }`}>
                {selected.has(a.id) && <Check className="w-3 h-3 text-nightblue" />}
              </span>
            </button>
          ))}
        </div>

        <DialogFooter>
          <div className="text-xs text-textgray mr-auto">
            {selected.size} seleccionada{selected.size === 1 ? "" : "s"}
          </div>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none">Cancelar</Button>
          <Button
            onClick={confirm}
            disabled={selected.size === 0}
            className="bg-nightblue hover:bg-blueblack text-white rounded-none uppercase text-xs tracking-widest font-semibold"
          >
            {multi ? "Agregar seleccionadas" : "Usar noticia"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
